import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TimescaleField } from 'components/Field/TimescaleField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { animateNext } from 'lib/animateNext';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_ACTIONCLOSED,
  REMOTE_ASESETUP_ACTIONSAVE,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import {
  selectAseSetupActionId,
  selectRemoteAseSetupAction,
  selectRemoteCategories,
  selectRemoteCategoryGroups,
  selectRemoteDueTypes,
  selectRemoteSeverities,
  selectRemoteTimescales,
} from 'store/remote/selectors';
import { AssessmentSuggestions } from 'store/remote/types';
import {
  DeprecatedDueDeterminedBy,
  TimescaleArray,
  TimescalePeriod,
} from 'types/types';
import {
  TextField,
  TextFieldRef,
  TextFieldSpecial,
} from 'components/Field/TextField';

export const ActionScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYACTION>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { incomingValueNewIdKey } = route.params;
  const costFieldRef = React.useRef<TextFieldRef>(null);
  const incomingValueKeyField = 'AseSetupActionField';
  const incomingValueField = useSelector((state) =>
    selectIncomingValue(state, incomingValueKeyField),
  ) as Record<string, number> | undefined;

  const actionId = useSelector(selectAseSetupActionId);

  const action: AssessmentSuggestions['actions'][number] = useSelector(
    (state) => selectRemoteAseSetupAction(state, actionId || -1),
  ) || {
    id: 0,
    what: '',
    why: '',
    categoryId: 0,
    severityId: 0,
    timescaleId: 0,
    negativeChecklistItemIds: [],
    positiveChecklistItemIds: [],
    cost: '',
    answerIds: [],
    due: {
      determinedBy: DeprecatedDueDeterminedBy.priority,
      timescale: null,
      typeId: null,
    },
  };

  const categories = useSelector(selectRemoteCategories)!;
  const categoryGroups = useSelector(selectRemoteCategoryGroups)!;
  const severities = useSelector(selectRemoteSeverities)!;
  const timescales = useSelector(selectRemoteTimescales)!;
  const dueTypes = useSelector(selectRemoteDueTypes)!;

  const [editing, setEditing] = React.useState<
    undefined | 'severity' | 'timescale' | 'due'
  >(undefined);
  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(action));

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== action.id) {
    data.current = cloneDeep(action);
  }

  const checkIsDirty = () =>
    !isEqual(action, data.current) || !isEqual(action.due, data.current.due);

  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const category = categories[data.current.categoryId] ?? categories[0]!;
  const severity = severities[data.current.severityId] ?? severities[0]!;
  const timescale = timescales[data.current.timescaleId] ?? timescales[0]!;
  const dueType = dueTypes[data.current.due.typeId || -1];

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = () => {
    if (data.current.what.trim() === '') {
      alert({ text: 'Please complete "What is the action?"' });
      return;
    }
    dispatch(
      REMOTE_ASESETUP_ACTIONSAVE({
        id: actionId,
        data: data.current,
        incomingValueNewIdKey,
      }),
    );
  };

  const handleSavePress = () => {
    if (isDirty.current || !action.id) {
      handleSave();
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const handleCategoryPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Category',
          fieldName: 'categoryId',
          incomingValueKey: incomingValueKeyField,
          returnType: 'number',
          value: data.current.categoryId,
          options: sortBy(
            Object.values(categories).map((cat) => ({
              name: cat.name,
              groupName: categoryGroups[cat.groupId]!.name,
              internalValue: cat.id,
              ordering:
                (cat.id === 0 ? 0 : categoryGroups[cat.groupId]!.name) +
                cat.name,
            })),
            ['ordering'],
          ),
        },
      }),
    );

  const handleSetSeverity = (newValue: number) => {
    handleChangeWithName('severityId', newValue);
    setEditing(undefined);
  };

  const handleSetTimescale = (newValue: number) => {
    handleChangeWithName('timescaleId', newValue);
    setEditing(undefined);
  };

  const handleSetDueType = (
    determinedBy: DeprecatedDueDeterminedBy,
    dueTypeId?: number,
  ) => {
    if (determinedBy === DeprecatedDueDeterminedBy.type && dueTypeId) {
      data.current.due.determinedBy = DeprecatedDueDeterminedBy.type;
      data.current.due.typeId = dueTypeId;
      data.current.due.timescale = null;
    } else if (determinedBy === DeprecatedDueDeterminedBy.timescale) {
      data.current.due.determinedBy = DeprecatedDueDeterminedBy.timescale;
      data.current.due.typeId = null;
      data.current.due.timescale = data.current.due.timescale || [
        1,
        TimescalePeriod.month,
      ];
    } else {
      data.current.due.determinedBy = DeprecatedDueDeterminedBy.priority;
      data.current.due.typeId = null;
      data.current.due.timescale = null;
    }
    setEditing(undefined);
  };

  const handleSetCustomTimescale = (newTimescale: TimescaleArray) => {
    data.current.due.determinedBy = DeprecatedDueDeterminedBy.timescale;
    data.current.due.typeId = null;
    data.current.due.timescale = newTimescale;
    forceUpdate();
  };

  React.useEffect(() => {
    if (incomingValueField) {
      data.current = {
        ...data.current,
        ...incomingValueField,
      };
      dispatch(SET_INCOMING_VALUE({ key: incomingValueKeyField }));
    }
  }, [dispatch, incomingValueField]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_ACTIONCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={actionId ? 'Action' : 'New Action'}
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            actionId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card>
        <TextAreaField
          name="what"
          onChangeWithName={handleChangeWithName}
          placeholder="What is the action? (required)"
          value={data.current.what}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusNative={!data.current.id}
          autoFocusDelay
          showFieldTypeIcon={false}
        />
        <TextAreaField
          name="why"
          onChangeWithName={handleChangeWithName}
          placeholder="Why is it needed?"
          value={data.current.why}
          onEsc={handleClose}
          showFieldTypeIcon={false}
        />
      </Card>

      <Card>
        {/** Category */}
        {Object.keys(categories).length > 1 ? (
          <CardRow
            label="Category"
            value={category.name}
            icon={ICONS.TagLight}
            onPress={handleCategoryPress}
          />
        ) : (
          <CardRow
            label="Category"
            value="No categories are set up"
            icon={ICONS.TagLight}
            iconColor={theme.colors.fieldValueText}
            navigationIcon={ICONS.EmptySetLight}
          />
        )}
        {/** Severities */}
        {Object.keys(severities).length > 1 ? (
          editing === 'severity' ? (
            <Card expandedInline margin={false} marginHorizontal={false}>
              {sortBy(Object.values(severities), 'ordering').map((item) => (
                <CardRow
                  key={item.id}
                  label={item.name}
                  iconColor={`#${item.colour}`}
                  icon={ICONS.DiamondExclamationSolid}
                  onPress={() => handleSetSeverity(item.id)}
                  radioValue={data.current.severityId === item.id}
                />
              ))}
            </Card>
          ) : (
            <CardRow
              label="Severity"
              value={severity.name}
              iconColor={`#${severity.colour}`}
              icon={ICONS.DiamondExclamationSolid}
              onPress={() => animateNext(() => setEditing('severity'))}
            />
          )
        ) : (
          <CardRow
            label="Severity"
            value="No severities are set up"
            icon={ICONS.DiamondExclamationSolid}
            iconColor={theme.colors.fieldValueText}
            navigationIcon={ICONS.EmptySetLight}
          />
        )}
        {/** Timescales */}
        {Object.keys(timescales).length > 1 ? (
          editing === 'timescale' ? (
            <Card expandedInline margin={false} marginHorizontal={false}>
              {sortBy(Object.values(timescales), 'ordering').map((item) => (
                <CardRow
                  key={item.id}
                  label={item.name}
                  onPress={() => handleSetTimescale(item.id)}
                  radioValue={data.current.timescaleId === item.id}
                />
              ))}
            </Card>
          ) : (
            <CardRow
              label="Timescale"
              value={timescale.name}
              icon={ICONS.HourglassLight}
              onPress={() => animateNext(() => setEditing('timescale'))}
            />
          )
        ) : (
          <CardRow
            label="Timescale"
            value="No timescales are set up"
            icon={ICONS.HourglassLight}
            iconColor={theme.colors.fieldValueText}
            navigationIcon={ICONS.EmptySetLight}
          />
        )}
        {/** Due Types */}
        {editing === 'due' ? (
          <Card expandedInline margin={false} marginHorizontal={false}>
            <CardRow
              label="Based on Timescale"
              onPress={() =>
                handleSetDueType(DeprecatedDueDeterminedBy.priority)
              }
              radioValue={
                data.current.due.determinedBy ===
                DeprecatedDueDeterminedBy.priority
              }
            />
            <CardRow
              label="Custom Timescale"
              onPress={() =>
                handleSetDueType(DeprecatedDueDeterminedBy.timescale)
              }
              radioValue={
                data.current.due.determinedBy ===
                DeprecatedDueDeterminedBy.timescale
              }
            />
            {sortBy(Object.values(dueTypes), 'name').map((item) => (
              <CardRow
                key={item.id}
                label={item.name}
                onPress={() =>
                  handleSetDueType(DeprecatedDueDeterminedBy.type, item.id)
                }
                radioValue={
                  data.current.due.determinedBy ===
                    DeprecatedDueDeterminedBy.type &&
                  data.current.due.typeId === item.id
                }
              />
            ))}
          </Card>
        ) : data.current.due.determinedBy === DeprecatedDueDeterminedBy.type &&
          dueType ? (
          <CardRow
            label="Due"
            value={dueType.name}
            icon={ICONS.CalendarDayLight}
            onPress={() => animateNext(() => setEditing('due'))}
          />
        ) : data.current.due.determinedBy ===
          DeprecatedDueDeterminedBy.timescale ? (
          <ViewWithSeparators horizontal>
            <CardRow
              label="Due"
              value="Custom Timescale"
              icon={ICONS.CalendarDayLight}
              onPress={() => animateNext(() => setEditing('due'))}
              style={{ flex: 1 }}
            />
            <CardRow
              navigationIcon={ICONS.HourglassLight}
              style={{
                backgroundColor: 'white',
              }}
              contentStyle={{
                paddingVertical: 0,
                flexDirection: 'row',
                flex: Platform.OS === 'web' ? undefined : 0,
                width: 100,
              }}
              childrenStyle={{
                width: 100,
              }}
            >
              <TimescaleField
                onChange={handleSetCustomTimescale}
                timescale={data.current.due.timescale}
              />
            </CardRow>
          </ViewWithSeparators>
        ) : (
          <CardRow
            label="Due"
            value="Based on Timescale"
            icon={ICONS.CalendarDayLight}
            onPress={() => animateNext(() => setEditing('due'))}
          />
        )}
        <CardRow
          label="Estimated Cost"
          onPress={() => costFieldRef.current?.focus()}
          contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
          icon={ICONS.SterlingSignLight}
        >
          <TextField
            myRef={costFieldRef}
            placeholder="No Cost"
            value={action.cost !== undefined ? action.cost : ''}
            name="cost"
            onChangeWithName={handleChangeWithName}
            special={TextFieldSpecial.currency}
            onEsc={handleClose}
            containerStyle={{ backgroundColor: 'transparent', width: 120 }}
            inputStyle={{ textAlign: 'right', paddingRight: 0 }}
          />
        </CardRow>
      </Card>
    </PageScrollView>
  );
};
